import React, { useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  message,
} from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import complianceService, { type InboundEInvoiceDto } from '@/services/compliance.service';

const InboundEInvoicesPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [matchRow, setMatchRow] = useState<InboundEInvoiceDto | null>(null);
  const [form] = Form.useForm();

  const { data: rows = [], isLoading, refetch } = useQuery({
    queryKey: ['asp-inbound'],
    queryFn: () => complianceService.listInbound(),
  });

  const matchMutation = useMutation({
    mutationFn: (body: { id: string; document_type: string; document_id: string }) =>
      complianceService.matchInbound(body.id, {
        document_type: body.document_type,
        document_id: body.document_id,
      }),
    onSuccess: () => {
      message.success('Matched');
      setMatchRow(null);
      queryClient.invalidateQueries({ queryKey: ['asp-inbound'] });
    },
    onError: (err: any) =>
      message.error(err?.response?.data?.message || err?.message || 'Match failed'),
  });

  const ignoreMutation = useMutation({
    mutationFn: (id: string) => complianceService.ignoreInbound(id),
    onSuccess: () => {
      message.success('Ignored');
      queryClient.invalidateQueries({ queryKey: ['asp-inbound'] });
    },
  });

  const columns: ColumnsType<InboundEInvoiceDto> = [
    {
      title: 'Seller',
      key: 'seller',
      render: (_, r) => (
        <div>
          <div>{r.seller_name || '—'}</div>
          <div style={{ color: '#888', fontSize: 12 }}>{r.seller_vat_trn || ''}</div>
        </div>
      ),
    },
    { title: 'Document', dataIndex: 'document_id', key: 'document_id' },
    {
      title: 'Issue date',
      dataIndex: 'issue_date',
      key: 'issue_date',
      render: (d) => (d ? dayjs(d).format('DD-MMM-YYYY') : '—'),
    },
    {
      title: 'Amount',
      key: 'amount',
      render: (_, r) =>
        r.payable_amount != null ? `${r.currency_code || 'AED'} ${r.payable_amount}` : '—',
    },
    {
      title: 'Match',
      dataIndex: 'match_status',
      key: 'match_status',
      render: (s: string) => (
        <Tag color={s === 'Matched' ? 'green' : s === 'Ignored' ? 'default' : 'orange'}>{s}</Tag>
      ),
    },
    {
      title: 'Linked',
      key: 'linked',
      render: (_, r) =>
        r.matched_document_id ? (
          <Button
            type="link"
            style={{ padding: 0 }}
            onClick={() => {
              const path =
                r.matched_document_type === 'DirectPurchaseInvoice'
                  ? `/fin/direct-purchase-invoices/${r.matched_document_id}`
                  : `/scm/procurement/supplier-invoices/${r.matched_document_id}`;
              navigate(path);
            }}
          >
            Open {r.matched_document_type}
          </Button>
        ) : (
          '—'
        ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, r) =>
        r.match_status === 'Unmatched' ? (
          <Space>
            <Button size="small" type="primary" onClick={() => setMatchRow(r)}>
              Match
            </Button>
            <Button size="small" onClick={() => ignoreMutation.mutate(r.id)}>
              Ignore
            </Button>
          </Space>
        ) : null,
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Alert
        style={{ marginBottom: 24 }}
        type="info"
        showIcon
        message="Inbound Peppol e-invoices"
        description="Documents received via Aigentrix (invoice.received). Match them to Supplier Invoices or Direct Purchase Invoices for AP archive."
      />
      <Card
        title="Inbound e-invoice workbench"
        extra={
          <Button icon={<ReloadOutlined />} onClick={() => refetch()}>
            Refresh
          </Button>
        }
      >
        <Table
          rowKey="id"
          loading={isLoading}
          columns={columns}
          dataSource={rows}
          pagination={{ pageSize: 20 }}
          locale={{ emptyText: 'No inbound Peppol documents yet.' }}
        />
      </Card>

      <Modal
        title="Match inbound e-invoice"
        open={!!matchRow}
        onCancel={() => setMatchRow(null)}
        onOk={() => form.submit()}
        confirmLoading={matchMutation.isPending}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={(values) => {
            if (!matchRow) return;
            matchMutation.mutate({
              id: matchRow.id,
              document_type: values.document_type,
              document_id: values.document_id,
            });
          }}
        >
          <Form.Item
            name="document_type"
            label="Document type"
            initialValue="SupplierInvoice"
            rules={[{ required: true }]}
          >
            <Select
              options={[
                { value: 'SupplierInvoice', label: 'Supplier Invoice' },
                { value: 'DirectPurchaseInvoice', label: 'Direct Purchase Invoice' },
              ]}
            />
          </Form.Item>
          <Form.Item
            name="document_id"
            label="ORDAN document ID"
            rules={[{ required: true }]}
          >
            <Input placeholder="UUID of PI / DPI" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default InboundEInvoicesPage;
